import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal, Box, Button, IconButton, CircularProgress,
  TextField, InputAdornment, Checkbox, Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import EditNoteIcon from "@mui/icons-material/EditNote";
import RefreshIcon from "@mui/icons-material/Refresh";
import { modalStyle, GREEN, GREEN_DARK, GREEN_LIGHT, TEAL, TEAL_LIGHT } from "../constants";
 
const API = process.env.REACT_APP_API_URL;
 
function useServerList(endpoint, authHeadersFn, extraQuery = "", mapFn = null, dedupe = false) {
  const [items,   setItems]   = useState([]);
  const [search,  setSearch]  = useState("");
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [dSearch, setDSearch] = useState("");
  const timerRef = useRef(null);
  const SIZE = 30;
 
  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { setDSearch(search); setPage(1); }, 400);
    return () => clearTimeout(timerRef.current);
  }, [search]);
 
  // Reset ke halaman 1 setiap kali query tambahan berubah (misal category filter berubah)
  useEffect(() => {
    setPage(1);
  }, [extraQuery]);
 
  useEffect(() => {
    if (!endpoint) return;
    let ignore = false;
    setLoading(true);
    const searchParams = dSearch
      ? `&column_operator=LIKE&column_value=${encodeURIComponent(dSearch)}`
      : "";
    fetch(
      `${API}${endpoint}?page=${page}&size=${SIZE}&sort_by=name&sort_order=asc${searchParams}${extraQuery}`,
      { headers: authHeadersFn() }
    )
      .then(r => r.json())
      .then(r => {
        if (ignore) return;
        const rawItems = r?.data || [];
        let mapped = mapFn
          ? rawItems.map(mapFn)
          : rawItems.map(i => ({ id: String(i.id), name: i.name }));
 
        if (dedupe) {
          const seen = new Map();
          mapped.forEach(i => { if (!seen.has(i.id)) seen.set(i.id, i); });
          mapped = [...seen.values()];
        }
 
        mapped.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        setItems(mapped);
        setTotal(r?.pagination_info?.total || 0);
      })
      .catch(() => {})
      .finally(() => { if (!ignore) setLoading(false); });
 
    return () => { ignore = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, dSearch, page, extraQuery]);
 
  return { items, search, setSearch, page, setPage, total, loading: loading || (!!endpoint && items.length === 0 && total === 0), SIZE };
}
 
function Pill({ label, onRemove, color = TEAL }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: `${color}18`, border: `1px solid ${color}44`,
      borderRadius: 20, padding: "3px 10px 3px 12px",
      fontSize: 12, color, fontWeight: 500, whiteSpace: "nowrap",
    }}>
      {label}
      {onRemove && (
        <span onClick={onRemove} style={{
          cursor: "pointer", lineHeight: 1, fontSize: 15,
          opacity: 0.6, fontWeight: 700, marginLeft: 2,
        }}>×</span>
      )}
    </span>
  );
}
 
const STEPS = ["Pilih Catalog", "Kategori", "Brand", "Konfirmasi"];
 
function StepBar({ current }) {
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "0 22px", marginBottom: 24 }}>
      {STEPS.map((label, i) => {
        const done   = i < current;
        const active = i === current;
        const color  = done || active ? TEAL : "#d1d5db";
        const textCol = done ? TEAL : active ? "#111" : "#9ca3af";
        return (
          <React.Fragment key={i}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
              <div style={{
                width: 30, height: 30, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 700, fontSize: done ? 14 : 12,
                background: done ? TEAL : active ? "#fff" : "#f3f4f6",
                border: `2px solid ${color}`,
                color: done ? "#fff" : color,
                transition: "all .25s",
                boxShadow: active ? `0 0 0 4px ${TEAL}22` : "none",
              }}>
                {done ? "✓" : i + 1}
              </div>
              <span style={{ fontSize: 11, fontWeight: active ? 700 : 500, color: textCol, whiteSpace: "nowrap" }}>
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{
                flex: 1, height: 2, marginBottom: 20,
                background: done ? TEAL : "#e5e7eb",
                transition: "background .25s",
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
 
function InfoBanner({ children }) {
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 8,
      background: "#fffbeb", border: "1px solid #fcd34d",
      borderRadius: 8, padding: "10px 14px", marginBottom: 16,
      fontSize: 13, color: "#92400e", lineHeight: 1.6,
    }}>
      <span style={{ flexShrink: 0, fontSize: 15 }}>⚠️</span>
      <span>{children}</span>
    </div>
  );
}
 
function ModeSwitch({ mode, onChange, includeCount, excludeCount }) {
  const opts = [
    { key: "include", label: "Include", icon: "✅", color: TEAL,    count: includeCount },
    { key: "exclude", label: "Exclude", icon: "🚫", color: "#ef4444", count: excludeCount },
  ];
  return (
    <div style={{
      display: "inline-flex", border: "1.5px solid #e5e7eb", borderRadius: 10,
      padding: 3, background: "#f9fafb", marginBottom: 14, gap: 3,
    }}>
      {opts.map(o => {
        const active = mode === o.key;
        return (
          <button key={o.key} onClick={() => onChange(o.key)} type="button" style={{
            display: "flex", alignItems: "center", gap: 6,
            border: "none", borderRadius: 8, padding: "7px 16px",
            fontSize: 13, fontWeight: 700, cursor: "pointer",
            background: active ? o.color : "transparent",
            color: active ? "#fff" : "#6b7280",
            transition: "all .15s",
          }}>
            <span style={{ fontSize: 14 }}>{o.icon}</span>
            {o.label}
            {o.count > 0 && (
              <span style={{
                fontSize: 11, fontWeight: 700, borderRadius: 10, padding: "1px 7px",
                background: active ? "rgba(255,255,255,0.25)" : `${o.color}18`,
                color: active ? "#fff" : o.color,
              }}>
                {o.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
 
function FilterPanel({
  title, subtitle, icon, accentColor,
  items, selected, onToggle, onSelectAll,
  search, onSearchChange, page, onPageChange,
  total, pageSize, loadingItems,
}) {
  const totalPages        = Math.ceil(total / pageSize) || 1;
  const allChecked        = items.length > 0 && items.every(i => selected.has(i.id));
  const someChecked       = items.some(i => selected.has(i.id)) && !allChecked;
  const selectedOnPage    = items.filter(i => selected.has(i.id));
  const selectedElsewhere = selected.size - selectedOnPage.length;
 
  return (
    <div style={{
      border: `1.5px solid ${selected.size > 0 ? accentColor + "88" : "#e5e7eb"}`,
      borderRadius: 12, overflow: "hidden", background: "#fff",
      transition: "border-color .2s", display: "flex", flexDirection: "column",
    }}>
      <div style={{
        padding: "11px 15px", display: "flex", alignItems: "center", gap: 9,
        background: selected.size > 0 ? `${accentColor}0d` : "#f9fafb",
        borderBottom: "1px solid #f0f0f0",
      }}>
        <span style={{ fontSize: 20 }}>{icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: selected.size > 0 ? accentColor : "#374151" }}>{title}</div>
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>{subtitle}</div>
        </div>
        {selected.size > 0 && (
          <span style={{ fontSize: 12, fontWeight: 700, color: "#fff", background: accentColor, borderRadius: 14, padding: "3px 11px" }}>
            {selected.size} dipilih
          </span>
        )}
      </div>
 
      <div style={{ padding: "8px 12px", borderBottom: "1px solid #f5f5f5" }}>
        <TextField
          size="small" fullWidth placeholder="Ketik untuk mencari..."
          value={search} onChange={e => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {loadingItems ? <CircularProgress size={14} sx={{ color: "#d1d5db" }} /> : <SearchIcon sx={{ fontSize: 16, color: "#9ca3af" }} />}
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onSearchChange("")} sx={{ p: 0.3 }}>
                  <CloseIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            "& .MuiInputBase-input": { fontSize: 13, py: 0.8 },
            "& fieldset": { border: "none" },
            "& .MuiOutlinedInput-root": { background: "#f3f4f6", borderRadius: 2 },
          }}
        />
      </div>
 
      <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 12px", borderBottom: "1px solid #f5f5f5", background: "#fdfdfd" }}>
        <Checkbox
          size="small" checked={allChecked} indeterminate={someChecked}
          onChange={e => onSelectAll(e.target.checked, items)}
          disabled={items.length === 0}
          sx={{ p: 0.4, color: "#d1d5db", "&.Mui-checked": { color: accentColor }, "&.MuiCheckbox-indeterminate": { color: accentColor } }}
        />
        <span style={{ fontSize: 12, color: "#6b7280", flex: 1 }}>
          {selected.size > 0
            ? <><strong style={{ color: accentColor }}>{selected.size}</strong> item dipilih dari total {total}</>
            : "Pilih semua di halaman ini"}
        </span>
        {selected.size > 0 && (
          <span onClick={() => onSelectAll(false, items)}
            style={{ fontSize: 11, color: "#ef4444", cursor: "pointer", textDecoration: "underline", fontWeight: 500 }}>
            Hapus pilihan
          </span>
        )}
      </div>
 
      <div style={{ overflowY: "auto", height: 230 }}>
        {loadingItems ? (
          <div style={{ padding: 20, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <CircularProgress size={20} sx={{ color: accentColor }} />
            <span style={{ fontSize: 12, color: "#9ca3af" }}>Memuat data...</span>
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: "#9ca3af" }}>
            {search ? `Tidak ada hasil untuk "${search}"` : "Belum ada data"}
          </div>
        ) : items.map(item => {
          const checked = selected.has(item.id);
          return (
            <div key={item.id} onClick={() => onToggle(item)} style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "6px 14px", cursor: "pointer",
              background: checked ? `${accentColor}12` : undefined,
              borderLeft: checked ? `3px solid ${accentColor}` : "3px solid transparent",
              transition: "all .1s",
            }}>
              <Checkbox size="small" checked={checked} onChange={() => onToggle(item)}
                onClick={e => e.stopPropagation()}
                sx={{ p: 0.3, color: "#d1d5db", "&.Mui-checked": { color: accentColor } }}
              />
              <span style={{ fontSize: 13, color: checked ? accentColor : "#374151", fontWeight: checked ? 600 : 400, flex: 1 }}>
                {item.name}
              </span>
              {checked && <span style={{ fontSize: 12, color: accentColor }}>✓</span>}
            </div>
          );
        })}
      </div>
 
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderTop: "1px solid #f0f0f0", background: "#fafafa", gap: 8 }}>
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1 || loadingItems}
          style={{ display: "flex", alignItems: "center", gap: 4, border: `1.5px solid ${page <= 1 ? "#e5e7eb" : "#d1d5db"}`, borderRadius: 8, padding: "6px 12px", background: page <= 1 ? "#f9fafb" : "#fff", cursor: page <= 1 ? "default" : "pointer", color: page <= 1 ? "#c0c0c0" : "#374151", fontSize: 13, fontWeight: 500 }}>
          ← Sebelumnya
        </button>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: loadingItems ? "#d1d5db" : "#374151" }}>
            {loadingItems ? "Hal. — / —" : `Hal. ${page} / ${totalPages}`}
          </div>
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>{loadingItems ? "Memuat..." : `${total} item total`}</div>
        </div>
        <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages || loadingItems}
          style={{ display: "flex", alignItems: "center", gap: 4, border: `1.5px solid ${page >= totalPages ? "#e5e7eb" : "#d1d5db"}`, borderRadius: 8, padding: "6px 12px", background: page >= totalPages ? "#f9fafb" : "#fff", cursor: page >= totalPages ? "default" : "pointer", color: page >= totalPages ? "#c0c0c0" : "#374151", fontSize: 13, fontWeight: 500 }}>
          Berikutnya →
        </button>
      </div>
 
      {selected.size > 0 && (
        <div style={{ padding: "8px 13px", borderTop: "1px solid #f0f0f0", display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center", background: `${accentColor}06`, maxHeight: 80, overflowY: "auto" }}>
          {selectedOnPage.map(item => (
            <Pill key={item.id} label={item.name} onRemove={() => onToggle(item)} color={accentColor} />
          ))}
          {selectedElsewhere > 0 && (
            <span style={{ fontSize: 12, color: accentColor, fontStyle: "italic" }}>
              +{selectedElsewhere} dipilih di halaman lain
            </span>
          )}
        </div>
      )}
    </div>
  );
}
 
function ReviewRow({ label, color, icon, items, nameCache, fallbackLabel }) {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
      <span style={{ fontSize: 16, marginTop: 2 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color, marginBottom: 6 }}>{label}</div>
        {items.size === 0 ? (
          <span style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic" }}>Semua {fallbackLabel}</span>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {[...items].map(id => <Pill key={id} label={nameCache[id] || `ID: ${id}`} color={color} onRemove={null} />)}
          </div>
        )}
      </div>
    </div>
  );
}
 
export default function GenerateCatalogModal({
  open, onClose, onDone, onRefresh,
  authHeaders, reports = [], initialReport = null,
}) {
  const navigate = useNavigate();
 
  const [step,           setStep]           = useState(0);
  const [selectedReport, setSelectedReport] = useState(null);
  const [catMode,        setCatMode]        = useState("include");
  const [brandMode,      setBrandMode]      = useState("include");
  const [inclCat,        setInclCat]        = useState(new Set());
  const [exclCat,        setExclCat]        = useState(new Set());
  const [inclBrand,      setInclBrand]      = useState(new Set());
  const [exclBrand,      setExclBrand]      = useState(new Set());
  const [deleteExisting, setDeleteExisting] = useState(false);
  const [loading,        setLoading]        = useState(false);
  const [done,           setDone]           = useState(false);
  const [catNameCache,   setCatNameCache]   = useState({});
  const [brandNameCache, setBrandNameCache] = useState({});
 
  const catList = useServerList(open && step >= 1 ? "/api/v1/category-master" : null, authHeaders);
 
  // Brand list: filter berdasarkan kategori yang dipilih di Include.
  // - Ada category di Include -> pakai endpoint brand-category-master, lalu distinct by brand_master_id.
  // - Tidak ada category di Include -> tetap pakai endpoint brand-master seperti semula.
  const inclCatIds        = [...inclCat].join(",");
  const useBrandByCategory = inclCat.size > 0;
 
  const brandList = useServerList(
    open && step >= 2 ? (useBrandByCategory ? "/api/v1/brand-category-master" : "/api/v1/brand-master") : null,
    authHeaders,
    useBrandByCategory ? `&category_master_id=${encodeURIComponent(inclCatIds)}` : "",
    useBrandByCategory ? (i => ({ id: String(i.brand_master_id), name: i.brand_master })) : null,
    useBrandByCategory
  );
 
  useEffect(() => {
    if (open) setSelectedReport(initialReport || null);
  }, [open, initialReport]);
 
  useEffect(() => {
    if (!catList.items.length) return;
    setCatNameCache(prev => { const n = { ...prev }; catList.items.forEach(i => { n[i.id] = i.name; }); return n; });
  }, [catList.items]);
 
  useEffect(() => {
    if (!brandList.items.length) return;
    setBrandNameCache(prev => { const n = { ...prev }; brandList.items.forEach(i => { n[i.id] = i.name; }); return n; });
  }, [brandList.items]);
 
  const toggle    = setter => item => setter(prev => { const n = new Set(prev); n.has(item.id) ? n.delete(item.id) : n.add(item.id); return n; });
  const selectAll = setter => (checked, filteredItems) => setter(prev => { const n = new Set(prev); filteredItems.forEach(i => checked ? n.add(i.id) : n.delete(i.id)); return n; });
 
  const reset = () => {
    setStep(0); setSelectedReport(null);
    setCatMode("include"); setBrandMode("include");
    setInclCat(new Set()); setExclCat(new Set());
    setInclBrand(new Set()); setExclBrand(new Set());
    setCatNameCache({}); setBrandNameCache({});
    setDeleteExisting(false); setLoading(false); setDone(false);
  };
 
  const handleClose = () => { reset(); onClose(); };
 
  // "Tutup" — hanya tutup modal, tidak refresh
  const handleJustClose = () => { onDone(); reset(); };
 
  // "Tutup & Refresh" — navigate ke halaman detail dengan sinyal forceRefresh
  const handleCloseAndRefresh = () => {
    const targetId = selectedReport?.catalog_id;
    reset();
    onClose();
    navigate(`/stat-module/setup-catalog-report/${targetId}`, {
      replace: true,
      state: { forceRefresh: Date.now() },
    });
  };
 
  const canNext = () => step === 0 ? !!selectedReport : true;
 
  const handleRun = async () => {
    if (!selectedReport) return;
    setLoading(true);
    try {
      const body = {};
      if (inclCat.size)   body.include_category_ids = [...inclCat].map(String);
      if (exclCat.size)   body.exclude_category_ids = [...exclCat].map(String);
      if (inclBrand.size) body.include_brand_ids    = [...inclBrand].map(String);
      if (exclBrand.size) body.exclude_brand_ids    = [...exclBrand].map(String);
 
      const params = new URLSearchParams({ delete_existing: deleteExisting });
      const res = await fetch(
        `${API}/api/v1/catalog-report/${selectedReport.catalog_id}/catalog/generate?${params}`,
        { method: "POST", headers: authHeaders(), body: JSON.stringify(body) }
      );
 
      if (!res.ok) {
        let msg = `Generate gagal (${res.status})`;
        try {
          const errBody = await res.json();
          if (errBody?.detail) msg = errBody.detail;
        } catch {}
        throw new Error(msg);
      }
 
      setDone(true);
    } catch (e) {
      alert(e?.message || "Gagal menjalankan generate");
    } finally {
      setLoading(false);
    }
  };
 
  const isLastStep = step === STEPS.length - 1;
 
  const renderStep = () => {
 
    // ── Sukses ───────────────────────────────────────────────────────────────
    if (done) return (
      <div style={{ textAlign: "center", padding: "32px 0 16px" }}>
        <div style={{
          width: 72, height: 72, borderRadius: "50%",
          background: GREEN_LIGHT, display: "flex",
          alignItems: "center", justifyContent: "center",
          margin: "0 auto 20px", border: `2.5px solid ${GREEN}`,
          boxShadow: `0 0 0 10px ${GREEN}18`,
        }}>
          <CheckCircleIcon sx={{ color: GREEN, fontSize: 38 }} />
        </div>
        <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700 }}>Generate berhasil dijalankan!</h3>
        <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 20, lineHeight: 1.8 }}>
          Proses berjalan di background.<br />
          Klik <strong style={{ color: "#111" }}>Tutup &amp; Refresh</strong> untuk melihat hasilnya.
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          <Button
            onClick={handleJustClose}
            variant="outlined"
            sx={{
              textTransform: "none", fontSize: 14, minWidth: 130,
              borderColor: "#e5e7eb", color: "#6b7280",
              "&:hover": { borderColor: "#9ca3af", backgroundColor: "#f9fafb" },
              py: 1.2,
            }}
          >
            Tutup
          </Button>
          <Button
            onClick={handleCloseAndRefresh}
            variant="contained"
            startIcon={<RefreshIcon sx={{ fontSize: 17 }} />}
            sx={{
              textTransform: "none", fontSize: 14, minWidth: 180,
              backgroundColor: GREEN, "&:hover": { backgroundColor: GREEN_DARK },
              boxShadow: "none", py: 1.2,
            }}
          >
            Tutup & Refresh
          </Button>
        </div>
      </div>
    );
 
    // ── Step 0: Pilih Catalog ─────────────────────────────────────────────
    if (step === 0) return (
      <div>
        <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 16px", lineHeight: 1.7 }}>
          Pilih catalog yang akan digunakan sebagai target generate. Generate hanya bisa dilakukan setelah catalog dipilih.
        </p>
        <Autocomplete
          options={reports}
          getOptionLabel={(o) => o.catalog_name || ""}
          value={selectedReport}
          onChange={(_, v) => setSelectedReport(v)}
          isOptionEqualToValue={(o, v) => o.catalog_id === v?.catalog_id}
          renderInput={(params) => (
            <TextField {...params} label="Pilih Catalog" placeholder="Ketik untuk mencari catalog..." size="small"
              sx={{ "& .MuiInputBase-input": { fontSize: 13 } }} />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.catalog_id}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                <EditNoteIcon sx={{ fontSize: 15, color: TEAL }} />
                {option.catalog_name}
              </div>
            </li>
          )}
          noOptionsText="Tidak ada catalog"
          sx={{ mb: 2 }}
        />
        {selectedReport ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: TEAL_LIGHT, border: `1.5px solid ${TEAL}44`, borderRadius: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: TEAL, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <EditNoteIcon sx={{ color: "#fff", fontSize: 18 }} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#085041" }}>{selectedReport.catalog_name}</div>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", background: "#f9fafb", border: "1.5px dashed #e5e7eb", borderRadius: 10 }}>
            <EditNoteIcon sx={{ fontSize: 18, color: "#d1d5db" }} />
            <span style={{ fontSize: 13, color: "#9ca3af", fontStyle: "italic" }}>Belum ada catalog yang dipilih</span>
          </div>
        )}
        {reports.length === 0 && (
          <div style={{ marginTop: 14, background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#92400e" }}>
            ⚠️ Belum ada catalog. Buat catalog terlebih dahulu melalui halaman <strong>Kelola Catalog</strong>.
          </div>
        )}
      </div>
    );
 
    // ── Step 1: Kategori ──────────────────────────────────────────────────
    if (step === 1) return (
      <div>
        <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 10px", lineHeight: 1.7 }}>
          Pilih kategori yang ingin <strong>disertakan</strong> atau <strong>dilewati</strong> saat generate.
        </p>
        <InfoBanner>
          Jika <strong>Include</strong> dan <strong>Exclude</strong> dikosongkan, <strong>semua kategori</strong> akan di-generate otomatis.
        </InfoBanner>
        <ModeSwitch mode={catMode} onChange={setCatMode} includeCount={inclCat.size} excludeCount={exclCat.size} />
        {catMode === "include" ? (
          <FilterPanel title="Include" subtitle="Hanya kategori ini yang di-generate" icon="✅" accentColor={TEAL}
            items={catList.items} selected={inclCat} onToggle={toggle(setInclCat)} onSelectAll={selectAll(setInclCat)}
            search={catList.search} onSearchChange={catList.setSearch} page={catList.page} onPageChange={catList.setPage}
            total={catList.total} pageSize={catList.SIZE} loadingItems={catList.loading} />
        ) : (
          <FilterPanel title="Exclude" subtitle="Lewati kategori ini saat generate" icon="🚫" accentColor="#ef4444"
            items={catList.items} selected={exclCat} onToggle={toggle(setExclCat)} onSelectAll={selectAll(setExclCat)}
            search={catList.search} onSearchChange={catList.setSearch} page={catList.page} onPageChange={catList.setPage}
            total={catList.total} pageSize={catList.SIZE} loadingItems={catList.loading} />
        )}
      </div>
    );
 
    // ── Step 2: Brand ─────────────────────────────────────────────────────
    if (step === 2) return (
      <div>
        <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 10px", lineHeight: 1.7 }}>
          Pilih brand yang ingin <strong>disertakan</strong> atau <strong>dilewati</strong> saat generate.
        </p>
        <InfoBanner>
          Jika <strong>Include</strong> dan <strong>Exclude</strong> dikosongkan, <strong>semua brand</strong> akan di-generate otomatis.
          {useBrandByCategory && (
            <> Daftar brand di bawah sudah <strong>difilter</strong> berdasarkan kategori yang dipilih di Include.</>
          )}
        </InfoBanner>
        <ModeSwitch mode={brandMode} onChange={setBrandMode} includeCount={inclBrand.size} excludeCount={exclBrand.size} />
        {brandMode === "include" ? (
          <FilterPanel title="Include" subtitle="Hanya brand ini yang di-generate" icon="✅" accentColor={TEAL}
            items={brandList.items} selected={inclBrand} onToggle={toggle(setInclBrand)} onSelectAll={selectAll(setInclBrand)}
            search={brandList.search} onSearchChange={brandList.setSearch} page={brandList.page} onPageChange={brandList.setPage}
            total={brandList.total} pageSize={brandList.SIZE} loadingItems={brandList.loading} />
        ) : (
          <FilterPanel title="Exclude" subtitle="Lewati brand ini saat generate" icon="🚫" accentColor="#ef4444"
            items={brandList.items} selected={exclBrand} onToggle={toggle(setExclBrand)} onSelectAll={selectAll(setExclBrand)}
            search={brandList.search} onSearchChange={brandList.setSearch} page={brandList.page} onPageChange={brandList.setPage}
            total={brandList.total} pageSize={brandList.SIZE} loadingItems={brandList.loading} />
        )}
      </div>
    );
 
    // ── Step 3: Konfirmasi ────────────────────────────────────────────────
    if (step === 3) {
      const hasFilter = inclCat.size || exclCat.size || inclBrand.size || exclBrand.size;
      return (
        <div>
          <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 16px", lineHeight: 1.7 }}>
            Periksa konfigurasi sebelum menjalankan generate.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", marginBottom: 14, background: TEAL_LIGHT, border: `1px solid ${TEAL}44`, borderRadius: 8 }}>
            <EditNoteIcon sx={{ fontSize: 16, color: TEAL }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: "#085041" }}>Target: {selectedReport?.catalog_name}</span>
          </div>
          <div style={{ border: "1.5px solid #e5e7eb", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
            <div style={{ padding: "11px 16px", background: "#f9fafb", borderBottom: "1px solid #f0f0f0", fontSize: 13, fontWeight: 700, color: "#374151" }}>
              📋 Ringkasan Filter
            </div>
            <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: 16 }}>
              {!hasFilter ? (
                <div style={{ fontSize: 13, color: "#9ca3af", fontStyle: "italic", textAlign: "center", padding: "8px 0" }}>
                  Tidak ada filter — semua kategori &amp; brand akan di-generate.
                </div>
              ) : (
                <>
                  {(inclCat.size > 0 || exclCat.size > 0) && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af" }}>Kategori</div>
                      {inclCat.size > 0 && <ReviewRow icon="✅" label="Include" color={TEAL} items={inclCat} nameCache={catNameCache} fallbackLabel="kategori" />}
                      {exclCat.size > 0 && <ReviewRow icon="🚫" label="Exclude" color="#ef4444" items={exclCat} nameCache={catNameCache} fallbackLabel="kategori" />}
                    </div>
                  )}
                  {(inclBrand.size > 0 || exclBrand.size > 0) && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af" }}>Brand</div>
                      {inclBrand.size > 0 && <ReviewRow icon="✅" label="Include" color={TEAL} items={inclBrand} nameCache={brandNameCache} fallbackLabel="brand" />}
                      {exclBrand.size > 0 && <ReviewRow icon="🚫" label="Exclude" color="#ef4444" items={exclBrand} nameCache={brandNameCache} fallbackLabel="brand" />}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div onClick={() => setDeleteExisting(p => !p)} style={{
            display: "flex", alignItems: "flex-start", gap: 14, padding: "14px 16px", cursor: "pointer",
            border: `1.5px solid ${deleteExisting ? "#fca5a5" : "#e5e7eb"}`,
            borderRadius: 10, marginBottom: deleteExisting ? 10 : 0,
            background: deleteExisting ? "#fff5f5" : "#fafafa", transition: "all .15s",
          }}>
            <input type="checkbox" checked={deleteExisting} onChange={() => {}}
              style={{ marginTop: 2, accentColor: "#ef4444", cursor: "pointer", flexShrink: 0, width: 16, height: 16 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: deleteExisting ? "#dc2626" : "#374151" }}>
                Hapus data generate sebelumnya
              </div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 3, lineHeight: 1.5 }}>
                Data yang diinput manual tidak akan terhapus.
              </div>
            </div>
          </div>
          {deleteExisting && (
            <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#92400e", lineHeight: 1.6 }}>
              ⚠️ Semua data hasil generate sebelumnya akan dihapus sebelum generate baru dijalankan.
            </div>
          )}
        </div>
      );
    }
  };
 
  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{ ...modalStyle, width: 700, maxHeight: "92vh", display: "flex", flexDirection: "column", borderRadius: "16px", overflow: "hidden", p: 0 }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px 16px", borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: GREEN_LIGHT, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <AutoAwesomeIcon sx={{ color: GREEN, fontSize: 22 }} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>Generate Catalog Otomatis</div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>
                {done ? "Selesai — pilih tindakan selanjutnya" : `Langkah ${step + 1} dari ${STEPS.length} — ${STEPS[step]}`}
              </div>
            </div>
          </div>
          <IconButton size="small" onClick={handleClose} sx={{ color: "#9ca3af" }}><CloseIcon /></IconButton>
        </div>
 
        {/* Step bar */}
        {!done && (
          <div style={{ padding: "20px 24px 0" }}>
            <StepBar current={step} />
          </div>
        )}
 
        {/* Body */}
        <div style={{ overflowY: "auto", flex: 1, padding: "4px 24px 24px", minHeight: done ? undefined : 460 }}>
          {renderStep()}
        </div>
 
        {/* Footer — hanya tampil saat belum done */}
        {!done && (
          <div style={{
            padding: "14px 24px", borderTop: "1px solid #f0f0f0",
            display: "flex", justifyContent: step > 0 ? "space-between" : "flex-end",
            alignItems: "center", flexShrink: 0, background: "#fff",
          }}>
            {step > 0 && (
              <Button onClick={() => setStep(s => s - 1)} variant="outlined" startIcon={<ChevronLeftIcon />}
                sx={{ textTransform: "none", fontSize: 13, color: "#6b7280", borderColor: "#e5e7eb" }}>
                Kembali
              </Button>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <Button onClick={handleClose} variant="text" sx={{ textTransform: "none", color: "#9ca3af", fontSize: 13 }}>Batal</Button>
              {!isLastStep ? (
                <Button onClick={() => setStep(s => s + 1)} disabled={!canNext()} variant="contained" endIcon={<ChevronRightIcon />}
                  sx={{
                    textTransform: "none", fontSize: 13, boxShadow: "none", backgroundColor: TEAL,
                    "&:hover": { backgroundColor: TEAL, filter: "brightness(0.92)", boxShadow: "none" },
                    "&.Mui-disabled": { backgroundColor: "#e5e7eb", color: "#9ca3af" },
                  }}>
                  Lanjut
                </Button>
              ) : (
                <Button onClick={handleRun} disabled={loading} variant="contained"
                  sx={{
                    textTransform: "none", fontSize: 13, minWidth: 200, boxShadow: "none",
                    backgroundColor: deleteExisting ? "#ef4444" : GREEN,
                    "&:hover": { backgroundColor: deleteExisting ? "#dc2626" : GREEN_DARK, boxShadow: "none" },
                  }}>
                  {loading
                    ? <><CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} /> Menjalankan...</>
                    : <><AutoAwesomeIcon sx={{ fontSize: 16, mr: 0.8 }} /> Jalankan Generate</>}
                </Button>
              )}
            </div>
          </div>
        )}
      </Box>
    </Modal>
  );
}